import { useEffect, useState, type ReactNode } from "react";
import { api, buildIsStale, onBuildChanged } from "../lib/api";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useSession, roleLabel, isFirmSide } from "../lib/session";
import { initials } from "../lib/format";
import { ProfileDialog } from "../screens/ProfileDialog";
import { Button } from "../components/primitives";
import { BrandLockup } from "../components/BrandLockup";
import type { Role } from "../lib/api";

/**
 * The shell for every internal surface.
 *
 * Grounded in paper, because staff live here all day with dense records. The
 * left rail is persistent and the current engagement is pinned inside it: every
 * permission in this platform is engagement-scoped, so the most expensive
 * mistake available to a Lead is acting on the right object in the wrong
 * engagement. The interface never lets that be ambiguous.
 */

interface NavItem {
  to: string;
  label: string;
  /** The rail's key. One or two characters, monospaced. */
  key: string;
  roles: Role[];
}

/**
 * The rail has two levels, and that is the whole point.
 *
 * It used to be one flat list mixing two different kinds of thing: items
 * about the practice (Firm, Engagements, People) next to items about one
 * engagement (Command, Delivery, Files). The engagement-level ones had no
 * engagement to work on, so each resolved "the first engagement you can
 * reach" independently — meaning you could open Intuiva in the list, click
 * Files, and be shown a different client's documents. Nothing connected
 * them; with one engagement they coincided, which hid it.
 *
 * Now the engagement is a container. Outside one you see the firm. Inside
 * one the rail names it and everything below belongs to it. Like opening a
 * folder: while you are in it, what you see is what is in it.
 *
 * Release lives inside an engagement rather than here: it is the publication
 * moment under Motion in DESIGN.md, and what is being published always
 * belongs to one engagement. It was absent from both rails for a long time
 * for the honest reason that the screen did not exist, and a nav item that
 * reliably 404s teaches people the rail is unreliable.
 */
const FIRM_NAV: NavItem[] = [
  // Super Admin only — hiding it is courtesy; the refusal is
  // app.is_super_admin() inside the definer functions of migration 024.
  { to: "/firm", label: "Firm", key: "FM", roles: ["super_admin"] },
  { to: "/engagements", label: "Engagements", key: "EN", roles: ["super_admin", "principal_partner", "project_lead", "consultant"] },
  // Catch (050) — the pipeline before a client is a client. Super Admin and
  // Principal Partner see the whole of it; a Project Lead sees the prospects
  // they own, so the item means something different to each and is correct
  // for both. Hiding it from consultants is courtesy; the refusal is the
  // policies in 050, which admit no consultant or client to any of its three
  // tables. It sits next to Engagements because that is the pair: what we are
  // chasing, and what we won.
  {
    to: "/catch",
    label: "Catch",
    key: "CA",
    roles: ["super_admin", "principal_partner", "project_lead"],
  },
  // Super Admin only, matching app_user_insert in 004.
  { to: "/people", label: "People", key: "PE", roles: ["super_admin"] },
  // A consultant's own two surfaces. They are firm-level because they span
  // every engagement that person is on, which is exactly the question they
  // answer: what is mine, anywhere.
  { to: "/work", label: "My work", key: "WK", roles: ["consultant"] },
  // A consultant's own two surfaces. Offers stays in this list for them
  // alone — it is their surface and it means something even when empty.
  //
  // But an offer can be issued to ANYBODY. `assignment.consultant_user_id`
  // is a plain reference to `app_user` with no role constraint, "Bring
  // someone in" offers every non-client on the roster, and `pendingOffers`
  // filters on the person rather than their role. So a Project Lead could
  // be assigned to a second engagement, could accept it over the API, and
  // had no item in the rail and no hint the offer existed. It simply sat
  // there, granting nothing.
  //
  // Fixed below by asking the server rather than guessing from the role:
  // anyone holding an offer gets the item, whoever they are.
  { to: "/offers", label: "Offers", key: "OF", roles: ["consultant"] },
  // One route, one audience now. It used to be two names for two readings of
  // the same screen; since 051 `app.bench` refuses anyone who is not Super
  // Admin, Principal Partner or Project Lead, so there is no second reading
  // to name. What each of those three sees still differs — a Lead sees the
  // people below them, the two senior roles see the whole firm — but that is
  // the same screen answering honestly, not a different one.
  {
    to: "/bench",
    label: "Bench",
    key: "BE",
    roles: ["super_admin", "principal_partner", "project_lead"],
  },
  // The consultant's "My profile" item is gone, and deliberately: since 051
  // the profile is a dialog opened from the identity box at the foot of this
  // rail, which every firm-side role can reach from any screen. Leaving a nav
  // item pointing at /bench would send a consultant to a directory the server
  // now refuses them — a rail item that reliably 404s teaches people the rail
  // is unreliable.
];

/**
 * Built per engagement, because every destination needs its id.
 *
 * The order is the order you work in: what it is, where it stands, the work
 * itself, what is going out, what is filed, and how it is shaped.
 *
 * **Two of these were renamed.** "Command" said nothing about what the
 * screen does — it reports where the engagement stands and what is slipping,
 * so it is the **Desk**: what is on your desk for this engagement.
 * "Delivery" was ambiguous between *the work being delivered* and *the act
 * of delivering*; the screen is deliverables and the decisions on them, so
 * it is **Review**.
 *
 * **Work is new.** Workstreams existed and had nowhere to live: you could
 * create one in Setup and then do nothing with it, because `createTask` had
 * no route. Setup defines the shape; Work is where the work happens.
 */
function engagementNav(id: string): NavItem[] {
  return [
    { to: `/engagements/${id}`, label: "Overview", key: "OV", roles: ["super_admin", "principal_partner", "project_lead", "consultant"] },
    { to: `/engagements/${id}/desk`, label: "Desk", key: "DK", roles: ["super_admin", "principal_partner", "project_lead"] },
    { to: `/engagements/${id}/work`, label: "Work", key: "WK", roles: ["super_admin", "principal_partner", "project_lead", "consultant"] },
    // Notes. This screen has been renamed three times in a day and the
    // history is worth keeping, because each rename was a correction:
    // "Delivery" described nothing; "Review" was my error, made without
    // opening the file and assuming it was the deliverables screen; and
    // "Intake" was accurate about the machinery and wrong about the
    // person. What someone actually does here is write down what was
    // said. Every old path still redirects.
    { to: `/engagements/${id}/notes`, label: "Notes", key: "NT", roles: ["super_admin", "principal_partner", "project_lead", "consultant"] },
    { to: `/engagements/${id}/files`, label: "Files", key: "FL", roles: ["super_admin", "principal_partner", "project_lead", "consultant"] },
    // Bills is firm-side only, and not a consultant's: what a client is
    // charged is not something the people delivering the work need to see.
    // Who may *raise* one is narrower still — app.may_bill, Super Admin or
    // Principal Partner — and the screen asks the server rather than
    // guessing, so a Lead gets the figures without the controls.
    // Release: the one authored moment in the product, and the last screen
    // DESIGN.md specified that had never been built. Between Files and
    // Bills because that is the order of the work — what is filed, what
    // goes out, what is charged for.
    //
    // Firm-side and not a consultant's, matching app.may_release (role AND
    // reach). A consultant can still READ the board — they need to know
    // what the client has in order to talk to them — but the rail should
    // not send them to a screen whose every control is refused.
    { to: `/engagements/${id}/release`, label: "Release", key: "RL", roles: ["super_admin", "principal_partner", "project_lead"] },
    { to: `/engagements/${id}/bills`, label: "Bills", key: "BL", roles: ["super_admin", "principal_partner", "project_lead"] },
    // Setup is a manager's screen. Role is the approximation the rail can
    // make; the exact test is app.may_manage (role AND reach), which only
    // the server can answer, so the screen itself re-checks. A Lead without
    // reach on this engagement sees the item and is told why, rather than
    // every consultant being shown seven controls that get refused.
    { to: `/engagements/${id}/setup`, label: "Setup", key: "SU", roles: ["super_admin", "principal_partner", "project_lead"] },
  ];
}

/**
 * Which engagement the rail is inside, read from the address.
 *
 * From the URL rather than from a context, so a reload or a pasted link
 * lands in the same place. AppShell is not rendered beneath the `:id` route
 * in every case, so useParams is not available here.
 */
function engagementInPath(pathname: string): string | null {
  const m = /^\/engagements\/([0-9a-f-]{36})(\/|$)/i.exec(pathname);
  return m ? m[1]! : null;
}


/**
 * "The platform has been updated" — shown when the server starts
 * answering with a different build than this tab loaded.
 *
 * A single-page application holds the JavaScript it loaded until the
 * document is reloaded, so shipping a fix does nothing for an open tab
 * and the person in it has no way to know. They see the old behaviour,
 * report it as unfixed, and get told to hard-refresh. That happened
 * several times before this existed.
 *
 * A notice rather than an automatic reload: reloading out from under
 * somebody mid-form throws away what they were typing, and this platform
 * asks people to type briefs, notes and invoice lines that are a
 * nuisance to retype. They pick the moment.
 */
function UpdateBar() {
  const [stale, setStale] = useState(buildIsStale);

  useEffect(() => onBuildChanged(() => setStale(true)), []);

  if (!stale) return null;

  return (
    <div
      role="status"
      className="flex flex-wrap items-center justify-center gap-4 border-b border-ink/20 bg-sand/35 px-5 py-2.5 text-sm text-ink"
    >
      <span>
        The platform has been updated. This tab is still running the older
        version.
      </span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="border border-ink px-3 py-1 text-xs font-medium uppercase tracking-chip transition-colors hover:bg-ink hover:text-canvas"
      >
        Reload
      </button>
    </div>
  );
}

export function AppShell({
  children,
  engagement,
  world = "paper",
}: {
  children: ReactNode;
  /** The engagement in scope, when a surface has one. */
  engagement?: { reference: string; name: string; organisation: string };
  /**
   * Which ground the work area sits on. Paper everywhere except Firm, which
   * is deliberately the odd one out: it is the only surface that is about the
   * practice rather than a job, only one account can open it, and walking
   * into it should feel like a different room. Setting it here rather than
   * inside the screen means `.workspace-grid` — which paints from --canvas —
   * flips with it, so the faint 72px grid comes along instead of stranding a
   * dark panel on a warm page.
   */
  world?: "paper" | "ink";
}) {
  const { me, signOut } = useSession();
  const [navOpen, setNavOpen] = useState(false);
  const [offers, setOffers] = useState(0);
  const location = useLocation();

  // How many assignments are waiting on this person, asked once per mount.
  //
  // Cheap, and the alternative is worse: an offer nobody can see is an
  // offer that expires. Firm-side only — the client portal does not render
  // this shell, and a client is never assigned to anything.
  const firmSide = isFirmSide(me?.role);
  /**
   * Your own profile, opened from the identity box below.
   *
   * Lives on the shell rather than on a screen so it is reachable from
   * anywhere — the whole reason it stopped being a route. Rendered after
   * </header> so it is not inside the rail's own stacking or overflow
   * context, which would clip a modal that is meant to cover the page.
   */
  const [profileOpen, setProfileOpen] = useState(false);
  useEffect(() => {
    if (!firmSide) return;
    let live = true;
    void api
      .get<unknown[]>("/api/my-offers")
      .then((rows) => {
        if (live) setOffers(Array.isArray(rows) ? rows.length : 0);
      })
      // A rail that fails to load a count is a rail, not an error. The
      // Offers screen itself reports anything that is actually wrong.
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [firmSide, location.pathname]);

  if (!me) return null;

  const inEngagement = engagementInPath(location.pathname);
  const items = (inEngagement ? engagementNav(inEngagement) : FIRM_NAV).filter(
    (item) => item.roles.includes(me.role),
  );

  // Anyone holding an offer gets somewhere to answer it, whatever their
  // role. Appended rather than put in FIRM_NAV, so the Super Admin and the
  // Partners do not carry a permanently empty item — the rail should show
  // what is true now, not what is possible.
  if (!inEngagement && offers > 0 && !items.some((i) => i.to === "/offers")) {
    items.push({
      to: "/offers",
      label: "Offers",
      key: "OF",
      roles: [me.role],
    });
  }

  return (
    // The update notice sits above everything, outside the rail/content
    // split, because it is about the application itself rather than
    // anything on the page.
    <div className="flex min-h-screen flex-col">
      <UpdateBar />
      <div data-world="paper" className="flex min-h-screen flex-1 flex-col bg-canvas lg:flex-row">
      {/* ------------------------------------------------------- left rail --- */}
      <header
        className={
          "flex shrink-0 flex-col border-white/15 bg-[#0d0d0d] text-[#f7f7f5] lg:sticky lg:top-0 lg:h-screen lg:w-[19rem] lg:border-r " +
          (navOpen ? "border-b" : "border-b lg:border-b-0")
        }
      >
        <div className="flex items-center justify-between gap-3 px-6 py-6 lg:border-b lg:border-white/15 lg:px-8 lg:py-8">
          <NavLink to="/" aria-label="Arikekpar & Company">
            <BrandLockup caption="People platform" />
          </NavLink>

          <button
            type="button"
            onClick={() => setNavOpen((v) => !v)}
            aria-expanded={navOpen}
            aria-controls="shell-nav"
            className="border border-white/25 px-3 py-2 text-2xs uppercase tracking-chip text-white/75 lg:hidden"
          >
            {navOpen ? "Close" : "Menu"}
          </button>
        </div>

        {/* The engagement in scope. Pinned, not a dropdown that has to be
            opened to answer "which engagement am I in?". */}
        {engagement && (
          <div className="shrink-0 border-b border-white/15 px-6 py-5 lg:border-b lg:px-8">
            <p className="text-2xs uppercase tracking-[0.24em] text-white/45">Current engagement</p>
            <p className="mt-2 font-editorial text-lg font-medium leading-tight text-white">
              {engagement.name}
            </p>
            <p className="mt-1 text-xs text-white/50">
              <span className="font-mono">{engagement.reference}</span>
              <span aria-hidden> · </span>
              {engagement.organisation}
            </p>
            {/* The way out. Without it the rail is a room with no door: once
                the nav shows only this engagement's surfaces, there is
                nothing left that leads back to the firm. */}
            <Link
              to="/engagements"
              onClick={() => setNavOpen(false)}
              className="mt-3 inline-block text-xs text-white/55 underline-offset-4 hover:text-white hover:underline"
            >
              ← All engagements
            </Link>
          </div>
        )}

        <nav
          id="shell-nav"
          aria-label="Sections"
          className={
            // No scrolling. A sidebar that scrolls is a sidebar that does
            // not fit, so the fix is always to make it fit rather than to
            // hide the overflow behind a gesture nobody can see is
            // available.
            //
            // The original bug: the rail is a flex column pinned to
            // `lg:h-screen`, so when the engagement nav grew to seven the
            // column overflowed and pushed the identity block and Sign out
            // off the bottom. Three things bought that space back — a
            // tighter nav inside an engagement, less padding around it,
            // and the single-line identity block below.
            //
            // Release makes eight, and the arithmetic was done rather than
            // guessed at, because this is the second time the rail has
            // been asked to grow. On the tightest real laptop (768px tall,
            // ~678px of viewport after browser chrome) the fixed parts come
            // to about 326px — brand 109, engagement block 133, nav padding
            // 24, identity 60 — leaving roughly 352px for the items
            // themselves. Eight at the old 44px is 352: exactly the budget,
            // with nothing spare for a long engagement name wrapping to a
            // second line. Eight at 40px is 320, which leaves 32px of
            // slack. So the desktop row drops one step.
            //
            // Touch targets stay 48px below `lg`, where the page scrolls
            // normally and there is no constraint. Only the desktop rail
            // tightens, and only inside an engagement — the firm rail has
            // at most five items and keeps its full height.
            "flex-1 px-3 lg:px-5 " +
            (inEngagement ? "py-3 lg:py-3 " : "py-5 ") +
            (navOpen ? "block" : "hidden lg:block")
          }
        >
          {items.map((item) => {
            // Overview is the engagement root, so a prefix match would
            // light it up on every child route too.
            const isRoot =
              item.to === "/" ||
              /^\/engagements\/[0-9a-f-]{36}$/i.test(item.to);
            const active = isRoot
              ? location.pathname === item.to
              : location.pathname.startsWith(item.to);

            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setNavOpen(false)}
                className={
                  "group relative flex items-center gap-3 border-l px-4 text-base transition-colors " +
                  "duration-150 ease-damped " +
                  // 48px everywhere a finger might land; 40px on the
                  // desktop rail inside an engagement, where there are
                  // eight of them and a mouse. See the arithmetic above.
                  (inEngagement
                    ? "min-h-12 py-3 lg:min-h-10 lg:py-1.5 "
                    : "min-h-12 py-3 ") +
                  (active
                    ? "border-sand bg-white/[0.09] text-white"
                    : "border-transparent text-white/72 hover:bg-white/[0.05] hover:text-white")
                }
              >
                {/* The detent: a filled square on the rule when live. The nav is
                    the rail, so it carries the same mark. */}
                <span
                  aria-hidden
                  className={
                    "h-[7px] w-[7px] shrink-0 border " +
                    (active
                      ? "border-sand bg-sand"
                      : "border-white/45 bg-transparent group-hover:border-white")
                  }
                />
                <span className="flex-1">{item.label}</span>
                {/* The one place a count replaces the key. Offers is the
                    only item in the rail that can be urgent and empty on
                    consecutive days, and a Lead who has never had one needs
                    telling that this is why it appeared. */}
                <span
                  className={
                    "font-mono text-2xs uppercase tracking-chip " +
                    (item.to === "/offers" && offers > 0
                      ? "text-sand"
                      : "text-white/35")
                  }
                >
                  {item.to === "/offers" && offers > 0 ? offers : item.key}
                </span>
              </NavLink>
            );
          })}
        </nav>

        {/* ----------------------------------------------------- who am I ---
            Two heights, because the rail has two heights.

            Outside an engagement there are at most five nav items and the
            full block fits: a 48px avatar, two lines, and a Sign out
            button beneath.

            Inside one there are eight, and on a laptop that pushed Sign
            out below the fold — a control you could not reach without
            knowing to scroll a sidebar that does not look scrollable. So
            inside an engagement this collapses to a single line of text:
            no avatar, no button. Roughly 100px of vertical space back,
            which is the difference.

            Sign out is not lost. The rail already carries "← All
            engagements" directly above, and one click up the full block
            returns. A session also still ends by expiry and by the server
            revoking it; this changes where the button lives, not whether
            you can end a session. */}
        <div
          className={
            "shrink-0 border-t border-white/15 px-6 lg:px-8 " +
            (inEngagement ? "py-4 " : "py-5 ") +
            (navOpen ? "block" : "hidden lg:block")
          }
        >
          {inEngagement ? (
            <p className="truncate text-xs text-white/55">
              <span className="text-white/80">{me.fullName}</span>
              <span aria-hidden> · </span>
              {roleLabel(me.role)}
            </p>
          ) : (
            <>
              {/* The identity block is the way into your own profile.
                  A real <button>, not a div with an onClick: it has to be
                  reachable by keyboard, announce itself as a control, and
                  carry an accessible name that says what it opens rather than
                  just reading out a person's name. `aria-haspopup="dialog"`
                  tells a screen reader a modal is coming. */}
              <button
                type="button"
                onClick={() => setProfileOpen(true)}
                aria-haspopup="dialog"
                aria-label={`Your profile — ${me.fullName}, ${roleLabel(me.role)}`}
                className="flex w-full items-center gap-3 border border-transparent px-1 py-1 text-left transition-colors duration-150 ease-damped hover:border-white/20 focus-visible:border-white/40"
              >
                <span
                  aria-hidden
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-sand/25 text-sm font-medium text-sand"
                >
                  {initials(me.fullName)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-white">
                    {me.fullName}
                  </span>
                  <span className="mt-0.5 block text-xs text-white/50">
                    {roleLabel(me.role)}
                  </span>
                </span>
              </button>
              <Button
                tone="quiet"
                onClick={() => void signOut()}
                className="mt-4 w-full justify-start border-white/15 px-3 text-white/65 hover:border-white/30 hover:text-white"
              >
                Sign out
              </Button>
            </>
          )}
        </div>
      </header>

      {/* ---------------------------------------------------------- content --- */}
      <main
        id="main"
        data-world={world}
        className="workspace-grid min-w-0 flex-1 px-5 py-7 text-ink sm:px-8 lg:px-12 lg:py-9 xl:px-16"
      >
        {children}
        </main>
      </div>

      {/* Firm-side only: the client portal does not render this shell, and a
          client has no bench entry to write. */}
      {firmSide && (
        <ProfileDialog
          open={profileOpen}
          onClose={() => setProfileOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * The screen's own head: a title set in the display face, and whatever the
 * screen affords, on one baseline.
 */
export function ScreenHead({
  title,
  sub,
  children,
}: {
  title: string;
  sub?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="mb-9 flex flex-wrap items-end justify-between gap-x-6 gap-y-5 border-b border-line pb-7">
      <div className="min-w-0">
        <p className="mb-5 text-2xs uppercase tracking-[0.28em] text-muted">
          {new Intl.DateTimeFormat("en-GB", {
            weekday: "long",
            day: "2-digit",
            month: "long",
            year: "numeric",
          }).format(new Date())}
        </p>
        <h1 className="font-display text-[clamp(2.35rem,4.7vw,4.25rem)] font-medium leading-[0.98] tracking-[-0.055em] text-ink">
          {title}
        </h1>
        {sub && <p className="mt-3 max-w-[64ch] text-[clamp(1rem,1.8vw,1.55rem)] leading-tight text-muted">{sub}</p>}
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}
